import { AlertTriangle, ArrowRight, Check, ClipboardList, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { COMMERCE } from '@/config/site';
import { getBrandName } from '@/data/brands';
import { useCartActions, useCartState, useCartSummary } from '@/features/cart/useCart';
import { buildListMessage } from '@/features/order/buildListMessage';
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/cn';
import { formatCents } from '@/lib/money';
import { WA_LINKS, whatsappLink } from '@/lib/whatsapp';

import { BruceHeadIcon, WhatsAppIcon } from '../common/icons';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogDescription, DialogTitle, SheetContent } from '../ui/dialog';
import { FreeShippingMeter } from './FreeShippingMeter';
import { ProductImage } from './ProductImage';
import { QuantityStepper } from './QuantityStepper';

export function CartDrawer() {
  const { isOpen } = useCartState();
  const { setCartOpen, closeCart, clearCart, setQuantity, removeItem, removeUnavailable } =
    useCartActions();
  const summary = useCartSummary();
  const { lines, itemCount, subtotalCents, unavailableLines, canCheckout } = summary;
  const reduceMotion = usePrefersReducedMotion();

  const [sent, setSent] = useState(false);
  const timerRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const isEmpty = lines.length === 0;

  /**
   * Abre a conversa com a lista pronta.
   *
   * A lista NÃO é limpa aqui: se o navegador bloquear o pop-up, o cliente
   * perderia tudo o que escolheu. Quem decide limpar é ele, no botão ao lado.
   */
  function handleSend() {
    window.open(whatsappLink(buildListMessage(summary)), '_blank', 'noopener,noreferrer');

    setSent(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setSent(false), 4000);

    toast.success('Lista enviada!', {
      description: 'Confira a conversa no WhatsApp para confirmar valores e entrega.',
    });

    // Import dinâmico: a gaveta vive no layout raiz, então um import estático
    // colocaria a biblioteca de confete no bundle de TODA página.
    if (!reduceMotion) {
      void import('canvas-confetti').then(({ default: confetti }) =>
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.7 },
          colors: ['#F46B1F', '#B77945', '#18B857', '#FFF7EF'],
        }),
      );
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" aria-describedby={undefined}>
        <header className="flex items-center gap-3 border-b border-sand-300 px-5 py-5 pr-14">
          <ClipboardList className="size-6 shrink-0 text-orange-500" aria-hidden="true" />
          <DialogTitle className="font-display text-lg font-extrabold text-brown-900">
            Minha lista
          </DialogTitle>
          {itemCount > 0 && (
            <Badge variant="primary" size="sm">
              {itemCount} {itemCount === 1 ? 'item' : 'itens'}
            </Badge>
          )}
        </header>

        {isEmpty ? (
          <EmptyCart onClose={closeCart} />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <FreeShippingMeter className="mb-4" />

              {/*
                Um item pode esgotar depois de entrar no carrinho — inclusive
                num carrinho restaurado do localStorage semanas depois. Some-lo
                em silêncio confunde ("cadê minha ração?"), então a linha fica,
                marcada, fora do total, e bloqueia o envio até ser resolvida.
              */}
              {unavailableLines.length > 0 && (
                <div className="mb-4 flex flex-col gap-2 rounded-2xl border-2 border-warning-500/40 bg-warning-500/10 p-3">
                  <p className="flex items-start gap-2 text-sm font-semibold text-brown-800">
                    <AlertTriangle
                      className="mt-0.5 size-4 shrink-0 text-orange-600"
                      aria-hidden="true"
                    />
                    {unavailableLines.length === 1
                      ? 'Um item da sua lista ficou sob encomenda.'
                      : `${unavailableLines.length} itens da sua lista ficaram sob encomenda.`}
                  </p>
                  <p className="text-xs text-brown-700">
                    Eles não entram na estimativa. Remova para enviar a lista, ou pergunte a
                    previsão de chegada na conversa.
                  </p>
                  <Button variant="outline" size="sm" onClick={removeUnavailable}>
                    <Trash2 aria-hidden="true" />
                    Remover indisponíveis
                  </Button>
                </div>
              )}

              <ul className="flex flex-col gap-3">
                {lines.map((line) => (
                  <li
                    key={line.sku}
                    className={cn(
                      'flex gap-3 rounded-2xl border-2 bg-white p-3',
                      line.isUnavailable
                        ? 'border-warning-500/50 bg-warning-500/5'
                        : 'border-sand-300',
                    )}
                  >
                    <Link
                      to={`/produto/${line.product.slug}`}
                      onClick={closeCart}
                      className="size-20 shrink-0 overflow-hidden rounded-xl bg-cream-200"
                    >
                      <ProductImage
                        product={line.product}
                        weightLabel={line.variant.label}
                        className="h-full w-full"
                      />
                    </Link>

                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <span className="font-display text-[11px] font-bold text-caramel-600 uppercase">
                        {getBrandName(line.product.brandId)}
                      </span>

                      <Link
                        to={`/produto/${line.product.slug}`}
                        onClick={closeCart}
                        className="line-clamp-2 font-display text-sm leading-snug font-bold text-brown-900 hover:text-orange-600"
                      >
                        {line.product.name}
                      </Link>

                      <span className="flex items-center gap-2 text-xs font-semibold text-brown-700">
                        {line.variant.label}
                        {line.isUnavailable && (
                          <Badge variant="warning" size="sm">
                            Sob encomenda
                          </Badge>
                        )}
                      </span>

                      <div className="mt-1 flex items-center justify-between gap-2">
                        <QuantityStepper
                          size="sm"
                          value={line.quantity}
                          removable
                          // Item esgotado só pode diminuir ou sair.
                          max={line.isUnavailable ? line.quantity : undefined}
                          onRemove={() => removeItem(line.sku)}
                          onChange={(quantity) => setQuantity(line.sku, quantity)}
                          label={`Quantidade de ${line.product.name}`}
                        />

                        {COMMERCE.showPrices && (
                          <span
                            className={cn(
                              'font-display text-sm font-extrabold',
                              line.isUnavailable
                                ? 'text-brown-700/50 line-through'
                                : 'text-brown-900',
                            )}
                          >
                            {formatCents(line.lineTotalCents)}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={clearCart}
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-brown-700 transition-colors hover:text-danger-500"
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
                Limpar lista
              </button>
            </div>

            <footer className="border-t border-sand-300 bg-cream-200 px-5 py-4">
              {/*
                Estimativa, não fatura: sem linha de frete cobrado e sem
                "Total". O valor final sai da conversa, não do site.
              */}
              {COMMERCE.showPrices && (
                <>
                  <dl className="mb-2 flex items-baseline justify-between">
                    <dt className="text-sm text-brown-700">Estimativa</dt>
                    <dd className="font-display text-xl font-extrabold text-brown-900">
                      {formatCents(subtotalCents)}
                    </dd>
                  </dl>

                  <p className="mb-3 text-[11px] leading-snug text-brown-700/80">
                    Valores de referência. A BrucePets confirma disponibilidade, valor final e
                    entrega pelo WhatsApp.
                  </p>
                </>
              )}

              <Button
                variant="whatsapp"
                size="lg"
                full
                onClick={handleSend}
                disabled={!canCheckout}
              >
                {sent ? <Check aria-hidden="true" /> : <WhatsAppIcon aria-hidden="true" />}
                {sent ? 'Lista enviada!' : 'Enviar lista pelo WhatsApp'}
              </Button>

              <Button variant="ghost" size="sm" full className="mt-1.5" onClick={closeCart}>
                Continuar escolhendo
              </Button>
            </footer>
          </>
        )}
      </SheetContent>
    </Dialog>
  );
}

function EmptyCart({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
      <BruceHeadIcon className="size-24 text-sand-300" />

      <div>
        <p className="font-display text-lg font-extrabold text-brown-900">Sua lista está vazia</p>
        <DialogDescription className="mt-1 text-sm text-brown-700">
          Escolha as rações do seu pet e envie tudo de uma vez pelo WhatsApp.
        </DialogDescription>
      </div>

      <div className="mt-2 flex w-full flex-col gap-2">
        <Button asChild full>
          <Link to="/catalogo" onClick={onClose}>
            Ver catálogo
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>

        <Button asChild variant="outline" full>
          <a href={WA_LINKS.help} target="_blank" rel="noopener noreferrer">
            <WhatsAppIcon aria-hidden="true" />
            Pedir ajuda no WhatsApp
          </a>
        </Button>
      </div>
    </div>
  );
}
